import { useFlowSchedules } from "@/api/flowSchedules";
import { ActionRow } from "@/components/rail-kit/ActionRow";
import { Accordion } from "@/components/ui/accordion";
import { AutomationsRailRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/automations/AutomationsRailRow";
import { useAutomationsRailCreateActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/automations/useAutomationsRailCreateActions";
import { RailGroup } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailGroup";
import { RailZeroState } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailZeroState";
import { railZeroStateCopy } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/railZeroStateCopy";
import { useActiveAutomationId } from "@/panes/automation/useActiveAutomationId";
import { CirclePause, Plus, Zap } from "lucide-react";

export function AutomationsRail() {
  const { data: schedules = [], isError } = useFlowSchedules();
  const activeAutomationId = useActiveAutomationId();
  const { openCreatePane } = useAutomationsRailCreateActions();

  if (isError) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-8">
        <p className="text-center text-sm text-n-40">
          Couldn&apos;t load automations.
        </p>
      </div>
    );
  }

  // State is derived: next_run_at set = the scheduler will fire it. Active
  // sorts soonest-first (the rail doubles as a "what happens next" view);
  // paused sorts by recency so the one you just paused stays findable.
  const activeSchedules = schedules
    .filter((schedule) => !!schedule.next_run_at)
    .sort(
      (a, b) =>
        new Date(a.next_run_at!).getTime() - new Date(b.next_run_at!).getTime(),
    );
  const pausedSchedules = schedules
    .filter((schedule) => !schedule.next_run_at)
    .sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );

  const activeRows = activeSchedules.map((schedule) => (
    <AutomationsRailRow
      key={schedule.id}
      schedule={schedule}
      active={activeAutomationId === schedule.id}
    />
  ));
  const pausedRows = pausedSchedules.map((schedule) => (
    <AutomationsRailRow
      key={schedule.id}
      schedule={schedule}
      active={activeAutomationId === schedule.id}
    />
  ));

  return (
    <div
      className="relative flex min-h-0 flex-1 flex-col"
      data-onboarding-target="rail-view-automations"
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-2 pt-3">
        <Accordion
          type="multiple"
          defaultValue={["automations-active", "automations-paused"]}
          className="flex flex-col gap-2"
        >
          <RailGroup
            groupId="automations-active"
            label="Active"
            icon={Zap}
            noCap
            items={activeRows}
            emptyState={
              <RailZeroState
                title={railZeroStateCopy.automations.active.title}
                description={railZeroStateCopy.automations.active.description}
              />
            }
          />
          <RailGroup
            groupId="automations-paused"
            label="Paused"
            icon={CirclePause}
            noCap
            items={pausedRows}
            emptyState={
              <RailZeroState
                title={railZeroStateCopy.automations.paused.title}
                description={railZeroStateCopy.automations.paused.description}
              />
            }
          />

          {/* A list's own closing verb — the quiet text-link register the
              other views use, never a boxed button. The agent path lives in
              the head glyph's create menu beside this one. */}
          <ActionRow
            icon={Plus}
            label="New automation"
            onClick={openCreatePane}
          />
        </Accordion>
      </div>
    </div>
  );
}
